/**
 * Every colour lives here. `theme.js` feeds these to createTheme, MUI emits them as
 * `--mui-palette-*`, and `style.css` only aliases them. White on an always-dark scrim is
 * MUI's `common.white`. A theme is a family × brightness tier.
 */
import {
  deriveRamp, shift, toOklch, fromOklch, capChroma, contrast,
} from './oklch';

/**
 * The accent is not the family's hue: status owns H21-142 and several spines sit inside
 * it (`sand` would accent as a warning). Instead the house blue leans toward the family,
 * compressed to ±ACCENT_RANGE so it never enters the status band.
 */
const ACCENT_BASE = 254;
const ACCENT_RANGE = 85;
const norm = (h) => ((h % 360) + 360) % 360;

function accentHue(spineHue) {
  let d = norm(spineHue - ACCENT_BASE);
  if (d > 180) d -= 360;
  return norm(ACCENT_BASE + (d / 180) * ACCENT_RANGE);
}

/**
 * Nudge `base` away from every surface in `against` until it clears `target` on all of
 * them, holding hue and chroma.
 */
function readableOn(base, against, target) {
  const { L, C, H } = toOklch(base);
  const lighter = against.every((bg) => toOklch(bg).L < L);
  for (let i = 0; i <= 40; i += 1) {
    const l = Math.max(0.05, Math.min(0.98, L + (lighter ? i : -i) * 0.01));
    const hex = fromOklch(l, capChroma(l, C, H), H);
    if (against.every((bg) => contrast(hex, bg) >= target)) return hex;
  }
  return base;
}

/**
 * A contained button's hover fill (MUI reads each colour's `dark`). Shifts AWAY from the
 * ink; MUI's darken-30% pushes a dark-ink fill toward its ink. The default mirrors MUI's
 * contrastText rule: white ink wherever white clears 3:1.
 */
function hoverFill(main, inkIsWhite = contrast(main, '#ffffff') >= 3) {
  return shift(main, inkIsWhite ? -0.06 : 0.06, 1);
}

/**
 * The most vivid colour at `hue` that clears `target` against `on`. Walks in from the vivid
 * end and stops at the first pass; the other direction returns near-black or near-white.
 * The 6:1 default leaves margin for the surface moving a tier.
 */
function vividOn(hue, on, isDark, target = 6.0, chroma = 0.34) {
  const from = isDark ? 0.40 : 0.78;
  const to = isDark ? 0.97 : 0.18;
  const stepBy = isDark ? 0.003 : -0.003;
  for (let L = from; isDark ? L <= to : L >= to; L += stepBy) {
    const hex = fromOklch(L, capChroma(L, chroma, hue), hue);
    if (contrast(hex, on) >= target) return hex;
  }
  return fromOklch(isDark ? 0.9 : 0.25, capChroma(isDark ? 0.9 : 0.25, chroma, hue), hue);
}

/**
 * A family is a spine hue plus `character` (0 neutral, 1 fully expressed). `oklch.js` turns
 * it into seven steps per tier:
 *
 *   bg  page   ·  paper  card   ·  raised  nested panel   ·  input  field well
 *   hairline  grouping border   ·  control  outlines, disabled fill   ·  strong  icons
 *
 * `pewter`/`graphite` are the near-neutrals, the only families exempt from the visible-tint
 * test. Traps:
 * - Raising `character` won't make light families more distinct; sRGB can't hold chroma at
 *   light's surface lightness (the chroma ceiling in `oklch.js`).
 * - The neutrals' `character` is relative to the chroma profile. Raise the profile without
 *   lowering it and they stop being neutral.
 */
export const DARK_FAMILIES = {
  slate: { hue: 255, character: 1.00 }, // Blue. The default.
  ember: { hue: 65, character: 1.00 }, // Warm brown.
  moss: { hue: 130, character: 1.00 }, // Deep green.
  orchid: { hue: 310, character: 1.00 }, // Muted violet.
  clay: { hue: 10, character: 1.00 }, // Deep brick.
  graphite: { hue: 195, character: 0.08 }, // Near-neutral. See above.
};

export const LIGHT_FAMILIES = {
  cool: { hue: 255, character: 1.00 }, // Blue. The light default.
  sand: { hue: 65, character: 1.00 }, // Warm tan.
  fern: { hue: 120, character: 1.00 }, // Yellow-green.
  mauve: { hue: 310, character: 1.00 }, // Muted plum.
  rose: { hue: 10, character: 1.00 }, // Warm blush.
  pewter: { hue: 195, character: 0.08 }, // Near-neutral. See above.
};

// Derived once at module load; `resolveRamp` runs on every palette build.
function buildTable(families, mode) {
  return Object.fromEntries(
    Object.entries(families).map(([name, { hue, character }]) => [name, {
      dim: deriveRamp(mode, hue, character, 'dim'),
      mid: deriveRamp(mode, hue, character, 'mid'),
      bright: deriveRamp(mode, hue, character, 'bright'),
    }]),
  );
}

export const DARK_THEMES = buildTable(DARK_FAMILIES, 'dark');
export const LIGHT_THEMES = buildTable(LIGHT_FAMILIES, 'light');
export const DEFAULT_DARK_THEME = 'slate';
export const DEFAULT_LIGHT_THEME = 'cool';
export const DEFAULT_TIER = 'mid';

/**
 * Resolve a theme id of the form "<family>" or "<family>.<tier>" for one scheme.
 * @param {'light'|'dark'} mode
 * @param {string} [id]
 */
export function resolveRamp(mode, id) {
  const table = mode === 'dark' ? DARK_THEMES : LIGHT_THEMES;
  const fallback = mode === 'dark' ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;
  const [family, tier] = String(id || '').split('.');
  const fam = table[family] || table[fallback];
  return fam[tier] || fam[DEFAULT_TIER];
}

/**
 * A theme's `character`, for surfaces pinned to a fixed lightness (the command bar), which
 * don't inherit family tint from the ramp.
 *
 * @param {'light'|'dark'} mode
 * @param {string} [id] - "<family>" or "<family>.<tier>"
 */
export function resolveCharacter(mode, id) {
  const table = mode === 'dark' ? DARK_FAMILIES : LIGHT_FAMILIES;
  const fallback = mode === 'dark' ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;
  const [family] = String(id || '').split('.');
  return (table[family] || table[fallback]).character;
}

/**
 * Status as TEXT on a card (4.5:1). Not reusable as a marker or a tinted surface; if one
 * ships, derive it per ramp and test it in every combination.
 *
 * `warning` is a transient condition (sync delayed), not a severity; severity is
 * elevated → critical → error. Pointing `warning` at `critical` turns every Alert red.
 */
const TEXT = {
  light: {
    primary: '#125eab',
    positive: '#13890b',
    critical: '#b32b23',
    warning: '#b04e02',
    error: '#842029',
  },
  dark: {
    primary: '#5ca3f6',
    positive: '#50b948',
    critical: '#ff7a6d',
    warning: '#ff792b',
    error: '#ed8080',
  },
};

// Badge chips, by what they sit on:
//   positive / primary : the page (nav tabs); white ink.
//   onAccent           : a contained primary Button, so it inverts against it.
const CHIP = {
  light: {
    positive: '#2e7d32',
    primary: '#125eab',
    primaryInk: '#ffffff',
    onAccent: '#e3f2fd',
    onAccentInk: '#0a3d73',
  },
  dark: {
    positive: '#32852c',
    primary: '#1a63b8',
    primaryInk: '#ffffff',
    onAccent: '#31496a',
    onAccentInk: '#ffffff',
  },
};

// Ticket-priority bar: flat lightness, severity by hue and chroma.
// KNOWN GAP: hue-only, so it collapses under colour-vision deficiency. Needs a second
// channel; deferred to a full data-table pass, don't patch in isolation.
const PRIORITY = {
  light: {
    low: '#5c7a99',
    normal: '#2e7d32',
    elevated: '#a86a00',
    critical: '#b3261e',
  },
  dark: {
    low: '#7fa6cc',
    normal: '#50b948',
    elevated: '#e0a800',
    critical: '#ff6b5e',
  },
};

// Dark's focus ring, and the command bar's in both schemes (navFocusRing).
const DARK_RING = '#7cb8ff';

/**
 * Build the palette for one scheme.
 * @param {'light'|'dark'} mode
 * @param {string} [themeId] - "<family>" or "<family>.<tier>"
 */
export function buildPalette(mode, themeId) {
  const n = resolveRamp(mode, themeId);
  const t = TEXT[mode];
  const chip = CHIP[mode];
  const prio = PRIORITY[mode];
  const isDark = mode === 'dark';
  const famCharacter = resolveCharacter(mode, themeId);

  // Chrome surfaces. Always shift AWAY from `paper`; deriving from `raised` puts light's
  // chrome on the far side of paper and the two shifts cancel.
  const cardHeader = shift(n.paper, isDark ? 0.027 : -0.028, 1.5, isDark ? 8 : 6);
  const tableHead = shift(n.paper, isDark ? 0.034 : -0.045, 1.8, isDark ? 10 : 8);
  // The side nav borders the page, not the card; that's the pair that must separate.
  const sideNav = shift(n.paper, isDark ? -0.014 : -0.008, 1.25, 3);

  /**
   * The command bar is dark in both schemes, with the family hue. Dark must track the page
   * (a fixed value reads raised or recessed depending on tier); light can pin it. Chroma
   * sits above the ramp's because tint fades near black; `character` still scales it.
   */
  const spineHue = toOklch(n.paper).H;
  const pageL = toOklch(n.bg).L;
  // Primary actions, links, active nav, and the selected-row hue.
  const accent = vividOn(accentHue(spineHue), n.paper, isDark);
  const accentSoft = shift(accent, isDark ? 0.10 : -0.08, 1.0);
  const commandBar = fromOklch(
    isDark ? Math.max(0.085, pageL - 0.055) : 0.205,
    (isDark ? 0.030 : 0.026) * famCharacter,
    spineHue,
  );

  // Zebra stripe: a neighbour of `paper`, not a white/black overlay (that washes out the
  // tint). Must clear ~2.5 lightness points AND stay off `archivedRow`, which recedes the
  // same way in light; tune the two together.
  const stripeSurface = isDark
    ? shift(n.paper, 0.035, 1.1)
    : shift(n.paper, -0.030, 1.3);

  /**
   * Selection signals by hue (the accent), hover by lightness; keep that split. An offset
   * from the card, never a fixed lightness. Ships as `primary.pale`; rows have no
   * selection state yet. Dark's offset is clamped, or `dim` bottoms out near black.
   */
  const cardL = toOklch(n.paper).L;
  const selectedDrop = isDark
    ? Math.min(0.070, Math.max(0.052, cardL * 0.16))
    : 0.086;
  const selectedRow = fromOklch(
    Math.max(0.10, Math.min(0.97, cardL - selectedDrop)),
    0.055,
    toOklch(accent).H,
  );
  /**
   * Hover, two forms, both on the family hue (the accent hue collides with selection):
   * - `rowHoverOverlay`: table rows. Translucent, composited as `background-image` over the
   *   row's base (paper, stripe or archived); a solid value would cross one of them.
   * - `hoverRow`: solid, for things painting their own background on the card (MUI's
   *   `action.hover`). Keyed to `paper`, so moving the stripe doesn't move it.
   */
  const hoverRow = fromOklch(
    Math.max(0.05, Math.min(0.98, toOklch(n.paper).L + (isDark ? 0.060 : -0.043))),
    toOklch(stripeSurface).C * 1.35,
    spineHue,
  );
  const hoverTint = fromOklch(isDark ? 0.95 : 0.18, 0.045, spineHue);
  const rowHoverOverlay = `${hoverTint}14`; // 0.08 alpha, as 8-digit hex

  const secondaryMain = readableOn(shift(n.strong, 0, 0.4), [isDark ? n.bg : '#ffffff'], 4.5);

  return {
    mode,
    primary: {
      main: accent,
      light: accentSoft,
      dark: hoverFill(accent),
      // Selected-row wash; see selectedRow above.
      pale: selectedRow,
    },

    // Neutral, for a state that's over rather than good or bad (an ended voucher). Must be
    // declared, or MUI renders Material purple; desaturated, or it reads as `info`.
    secondary: {
      main: secondaryMain,
      dark: hoverFill(secondaryMain, !isDark),
      contrastText: isDark ? n.bg : '#ffffff',
    },

    // Declare all four, or MUI substitutes Material defaults that fail AA in light.
    error: { main: t.error, dark: hoverFill(t.error) },
    warning: { main: t.warning, dark: hoverFill(t.warning) },
    success: { main: t.positive, dark: hoverFill(t.positive) },
    info: { main: t.primary, dark: hoverFill(t.primary) },

    background: { default: n.bg, paper: n.paper },
    text: {
      primary: isDark ? 'rgba(255, 255, 255, 0.90)' : 'rgba(0, 0, 0, 0.87)',
      secondary: isDark ? 'rgba(255, 255, 255, 0.70)' : 'rgba(0, 0, 0, 0.60)',
      disabled: isDark ? 'rgba(255, 255, 255, 0.50)' : 'rgba(0, 0, 0, 0.45)',
    },
    divider: n.hairline,
    action: {
      // Never the stripe value, or row hover becomes invisible app-wide.
      hover: hoverRow,
      selected: isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.08)',
      disabledBackground: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
    },

    tableHeader: tableHead,

    // Cards sit on `paper`, one step above the page. `raised` is for panels nested INSIDE
    // a card, which separate from the card.
    cardBackground: n.paper,
    subtleBackground: n.raised,
    cardHeaderBackground: cardHeader,
    sideNavBackground: sideNav,
    inputBackground: n.input,
    inputLabel: isDark ? 'rgba(255, 255, 255, 0.70)' : 'rgba(0, 0, 0, 0.60)',

    border: n.hairline,
    borderColor: isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.12)',
    borderControl: n.control,
    // Ink on a neutral fill (the switch track). Dark's `strong` is too light for white ink.
    neutral: { main: n.strong, contrastText: isDark ? n.bg : '#ffffff' },

    critical: t.critical,
    accent: { main: t.positive },
    status: {
      priorityLow: prio.low,
      priorityNormal: prio.normal,
      priorityElevated: prio.elevated,
      priorityCritical: prio.critical,
      // See CHIP above.
      positiveChip: chip.positive,
      primaryChip: chip.primary,
      primaryChipInk: chip.primaryInk,
      onAccentChip: chip.onAccent,
      onAccentChipInk: chip.onAccentInk,
    },

    /**
     * Archived: the outline (2px card, 1px row) must clear 3:1 on the card, so one value
     * per scheme. Outline and surface are colour only; the text is the ArchivedBadge /
     * ArchivedRowChip fill below (ArchiveSwitch.jsx).
     */
    archived: isDark ? '#e0a800' : '#8f6a00',
    archivedFill: isDark ? '#d9a406' : '#e8ae00',
    archivedInk: '#1f1703',

    // A recessed neutral, not a status tint (archived is lifecycle, not severity). Never
    // `subtleBackground`, which collapses onto the stripe in light.
    archivedRow: isDark
      ? shift(n.paper, -0.048, 0.75)
      : shift(n.paper, -0.075, 0.85),

    // Same value as `rowHover`, for hover outside tables (DataDisplay).
    primaryHover: hoverRow,

    link: accent,
    linkHover: accentSoft,

    // Validate against every surface a ring lands on (card, stripe, hover, selected and
    // archived rows), not just `paper`. Not inside the command bar: see `navFocusRing`.
    focusRing: isDark ? DARK_RING : '#0b4f96',
    // style.css re-scopes `--focus-ring` to this inside the AppBar.
    navFocusRing: DARK_RING,

    stripe: stripeSurface,

    /**
     * By role: tables and inline groups take none, `raised` resting cards, `overlay` menus,
     * `modal` dialogs. Dark lifts by a real surface step plus the rim (shadows don't read on
     * a dark ground), which is why the ramp must stay monotonic.
     */
    elevation: {
      raised: isDark
        ? '0 1px 2px rgba(0, 0, 0, 0.45), 0 4px 12px -2px rgba(0, 0, 0, 0.40)'
        : '0 1px 1.5px rgba(16, 24, 40, 0.06), 0 4px 10px -2px rgba(16, 24, 40, 0.09)',
      overlay: isDark
        ? '0 2px 4px rgba(0, 0, 0, 0.50), 0 8px 24px -4px rgba(0, 0, 0, 0.55)'
        : '0 2px 4px rgba(16, 24, 40, 0.07), 0 10px 26px -4px rgba(16, 24, 40, 0.13)',
      modal: isDark
        ? '0 4px 8px rgba(0, 0, 0, 0.55), 0 20px 48px -8px rgba(0, 0, 0, 0.65)'
        : '0 4px 8px rgba(16, 24, 40, 0.08), 0 24px 56px -12px rgba(16, 24, 40, 0.20)',
      // Top rim-light, layered on the drop shadow. Load-bearing in dark.
      rim: isDark
        ? 'inset 0 1px 0 rgba(255, 255, 255, 0.055)'
        : 'inset 0 1px 0 rgba(255, 255, 255, 0.85)',
      inset: isDark
        ? 'inset 0 1px 2px rgba(0, 0, 0, 0.28)'
        : 'inset 0 1px 2px rgba(16, 24, 40, 0.05)',
    },

    rowHover: hoverRow,
    rowHoverOverlay,

    placeholder: vividOn(spineHue, n.input, isDark, 4.5, 0.02),
    // Must clear 3:1 on both the card and the field well, which sit on opposite sides of
    // `strong` in dark (icons live in selects and date pickers too).
    iconMuted: readableOn(n.strong, [n.paper, n.input], 3.0),
    // Live secondary headings. Not `text.disabled`, which is for disabled content only.
    // Desaturated, or it reads as link blue in cool/slate.
    textMuted: readableOn(shift(n.strong, 0, 0.4), [sideNav, n.raised, n.paper], 5.0),
    // The favourite marker, on the side nav: #E4B600, adjusted only where it misses 3:1.
    star: readableOn('#E4B600', [sideNav], 3.0),

    navBackground: commandBar,

    // Both default to Material grey otherwise. Tooltip: the bar in light; in dark the bar
    // sits below the card and reads as a hole, so a lifted surface. MUI's ink stays white.
    Tooltip: { bg: isDark ? shift(n.paper, 0.14, 1.2) : commandBar },
    Chip: { defaultBorder: n.control },
    // Body-cell lines. MUI otherwise derives them from `divider`, darkened or lightened past
    // any family. On light archived rows this nearly matches the row; the outline marks them.
    TableCell: { border: n.hairline },

    // MUI paints the AppBar from `AppBar.darkBg` under a rule that beats a styled
    // `backgroundColor`, so set it here. Must track `navBackground`.
    AppBar: {
      darkBg: commandBar,
      darkColor: 'rgba(255, 255, 255, 0.60)',
    },
    // The accent for anything inside the command bar. Light's accent is derived against
    // the card and fails 4.5:1 on the bar, so it's lifted there; `primary.main` elsewhere.
    navAccent: contrast(accent, commandBar) >= 4.5
      ? accent
      : vividOn(accentHue(spineHue), commandBar, true, 4.5, 0.30),
    navText: 'rgba(255, 255, 255, 0.60)',
    navTextHover: 'rgba(255, 255, 255, 0.85)',
    navTextActive: '#ffffff',
    navBorder: 'rgba(255, 255, 255, 0.15)',
  };
}

export default buildPalette;
